import { Component, type ReactNode } from "react";
import { Button } from "@heroui/button";

const CHUNK_ERROR_PATTERN =
  /loading dynamically imported module|failed to fetch dynamically imported module|importing a module script failed|chunkloaderror/i;

interface RouteErrorBoundaryProps {
  // When this changes (i.e. the route changed) a boundary in the error state
  // resets itself, so navigating away from a broken route recovers.
  resetKey: string;
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render errors under the router — most importantly a lazy route whose
 * chunk fails to load (e.g. after a deploy changed the hashed filenames while
 * the tab stayed open). Instead of the previous silent `window.location.reload()`,
 * it shows a clear message with a user-initiated "Recharger" action.
 */
export default class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    const isChunkError = CHUNK_ERROR_PATTERN.test(error.message ?? "");

    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <span aria-hidden className="text-4xl">
          {isChunkError ? "✨" : "⚠️"}
        </span>
        <h1 className="text-xl font-semibold text-foreground">
          {isChunkError
            ? "Une nouvelle version est disponible"
            : "Une erreur est survenue"}
        </h1>
        <p className="text-sm text-default-500">
          {isChunkError
            ? "Rechargez la page pour continuer avec la dernière version."
            : "Réessayez, ou rechargez la page si le problème persiste."}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {!isChunkError && (
            <Button
              radius="full"
              variant="flat"
              onPress={() => this.setState({ error: null })}
            >
              Réessayer
            </Button>
          )}
          <Button
            color="primary"
            radius="full"
            onPress={() => window.location.reload()}
          >
            Recharger la page
          </Button>
        </div>
      </div>
    );
  }
}
